import { Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import { DeleteOutlined, EditOutlined, RetweetOutlined } from '@ant-design/icons'
import { ChatAttachments } from './ChatAttachments'
import type { ChatMessage } from './types'
import { formatChatTime, initials, linkifyParts, truncateText } from './utils'
import styles from './Chat.module.css'

interface Props {
  message: ChatMessage
  mine: boolean
  showAvatar: boolean
  showAuthor: boolean
  groupPos: 'solo' | 'first' | 'mid' | 'last'
  onReply: (m: ChatMessage) => void
  onEdit: (m: ChatMessage) => void
  onDelete: (m: ChatMessage) => void
  onJumpReply: (id: number) => void
}

export function ChatMessageItem({
  message,
  mine,
  showAvatar,
  showAuthor,
  groupPos,
  onReply,
  onEdit,
  onDelete,
  onJumpReply,
}: Props) {
  const deleted = Boolean(message.deleted_at)

  const menuItems: MenuProps['items'] = deleted
    ? []
    : [
        {
          key: 'reply',
          icon: <RetweetOutlined />,
          label: 'Ответить',
          onClick: () => onReply(message),
        },
        ...(mine
          ? [
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Изменить',
                onClick: () => onEdit(message),
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Удалить',
                danger: true,
                onClick: () => onDelete(message),
              },
            ]
          : []),
      ]

  const shapeClass =
    groupPos === 'solo'
      ? styles.bubbleSolo
      : groupPos === 'first'
        ? styles.bubbleFirst
        : groupPos === 'mid'
          ? styles.bubbleMid
          : styles.bubbleLast

  const bubble = (
    <div
      className={`${styles.bubble} ${shapeClass} ${deleted ? styles.bubbleDeleted : ''}`}
      data-message-id={message.id}
    >
      {showAuthor && !deleted ? <div className={styles.author}>{message.name}</div> : null}

      {message.reply_to && !deleted ? (
        <button
          type="button"
          className={styles.replyQuote}
          onClick={() => onJumpReply(message.reply_to!.id)}
        >
          <div className={styles.replyName}>{message.reply_to.name}</div>
          <div className={styles.replyText}>
            {truncateText(message.reply_to.text || 'Вложение', 50)}
          </div>
        </button>
      ) : null}

      {deleted ? (
        <div className={styles.body}>{message.name} удалил(а) сообщение</div>
      ) : (
        <>
          {message.text ? (
            <div className={styles.body}>
              {linkifyParts(message.text).map((p, i) =>
                p.type === 'link' ? (
                  <a
                    key={i}
                    className={styles.link}
                    href={p.value}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {p.value.length > 48 ? `${p.value.slice(0, 46)}…` : p.value}
                  </a>
                ) : (
                  <span key={i}>{p.value}</span>
                ),
              )}
            </div>
          ) : null}
          {message.attachments?.length ? <ChatAttachments urls={message.attachments} /> : null}
        </>
      )}

      <div className={styles.meta}>
        {!deleted && message.edited_at ? <span>изм.</span> : null}
        <span>{formatChatTime(message.created_at)}</span>
      </div>
    </div>
  )

  return (
    <div
      className={`${styles.row} ${mine ? styles.rowMine : ''} ${groupPos === 'mid' || groupPos === 'last' ? styles.rowTight : ''}`}
      id={`chat-msg-${message.id}`}
    >
      {!mine ? (
        showAvatar ? (
          <div className={styles.avatar} aria-hidden>
            {initials(message.name)}
          </div>
        ) : (
          <div className={styles.avatarSpacer} aria-hidden />
        )
      ) : null}

      {!deleted && menuItems?.length ? (
        <Dropdown menu={{ items: menuItems }} trigger={['contextMenu']}>
          {bubble}
        </Dropdown>
      ) : (
        bubble
      )}
    </div>
  )
}
